import { ChangeEvent, DragEvent, FC, useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { ReactComponent as UploadIcon } from 'src/assets/svg/bytesize_upload.svg'
import { useGetUserFeaturesQuery } from 'src/reactQuery/useGetUserFeaturesQuery'
import { ANALYTICS_API_URL } from 'src/services/constants/urls'
import {
  deleteUserAvatar,
  uploadUserAvatar,
} from 'src/services/userFeaturesApi'
import styles from './profileImage.module.scss'

interface ProfileImageProps {
  userName?: string
}

const MAX_AVATAR_SIZE = 5 * 1024 * 1024

const ALLOWED_AVATAR_TYPES = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
  'image/gif',
  'image/svg+xml',
]

const ProfileImage: FC<ProfileImageProps> = ({ userName = 'John' }) => {
  const {
    data: features,
    isLoading: isFeaturesLoading,
    refetch,
  } = useGetUserFeaturesQuery()
  const [busyAction, setBusyAction] = useState<'upload' | 'delete' | null>(null)
  const [isImageLoaded, setIsImageLoaded] = useState<boolean>(false)

  const isBusy = busyAction !== null
  const fileInputRef = useRef<HTMLInputElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)

  const savedAvatarUrl = features?.data?.avatarUrl
    ? `${ANALYTICS_API_URL}${features.data.avatarUrl}`
    : null

  // Reset the load flag whenever the avatar URL changes so the spinner shows
  // while the new image is fetched. Cached images may already have fired their
  // `load` event before React attached the handler, which would leave the
  // spinner stuck — so detect that synchronously via the element's `complete`.
  useEffect(() => {
    if (!savedAvatarUrl) {
      setIsImageLoaded(false)
      return
    }

    setIsImageLoaded(Boolean(imgRef.current?.complete))
  }, [savedAvatarUrl])

  // Spinner covers: the features query, an in-flight upload/delete, and the
  // time the browser spends fetching the resolved avatar image.
  const isLoading =
    isBusy || isFeaturesLoading || (!!savedAvatarUrl && !isImageLoaded)

  const isValidImage = (file: File): boolean => {
    if (!ALLOWED_AVATAR_TYPES.includes(file.type)) {
      toast.error('Only PNG, JPG, WEBP, GIF and SVG images are allowed')
      return false
    }

    if (file.size > MAX_AVATAR_SIZE) {
      toast.error('Image must be 5MB or smaller')
      return false
    }

    return true
  }

  const uploadFile = async (file: File): Promise<void> => {
    if (!isValidImage(file)) {
      return
    }

    setBusyAction('upload')

    try {
      await uploadUserAvatar(file)
      await refetch()
      toast.success('Profile image updated')
    } catch (error) {
      toast.error('Failed to upload image')
    } finally {
      setBusyAction(null)

      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]

    if (file) {
      uploadFile(file)
    }
  }

  const handleDelete = async () => {
    if (!savedAvatarUrl || isBusy) {
      return
    }

    setBusyAction('delete')

    try {
      await deleteUserAvatar()
      await refetch()
      toast.success('Profile image removed')
    } catch (error) {
      toast.error('Failed to remove image')
    } finally {
      setBusyAction(null)
    }
  }

  const handleUpdate = () => {
    fileInputRef.current?.click()
  }

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()

    const file = event.dataTransfer.files?.[0]

    if (file && file.type.startsWith('image/')) {
      uploadFile(file)
    }
  }

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
  }

  return (
    <div className={styles.profileImage}>
      <h3 className={styles.title}>Image</h3>

      <div className={styles.profileSection}>
        <div className={styles.avatar}>
          {savedAvatarUrl ? (
            <img
              alt="Profile"
              onError={() => setIsImageLoaded(true)}
              onLoad={() => setIsImageLoaded(true)}
              ref={imgRef}
              src={savedAvatarUrl}
            />
          ) : (
            !isLoading && (
              <div className={styles.placeholder}>
                {userName.charAt(0).toUpperCase()}
              </div>
            )
          )}

          {isLoading && (
            <div className={styles.avatarLoading}>
              <span className={styles.spinner} />
            </div>
          )}
        </div>

        <div className={styles.userInfo}>
          <p className={styles.userName}>{userName}</p>
          <div className={styles.actions}>
            <button
              className={styles.deleteBtn}
              disabled={!savedAvatarUrl || isBusy}
              onClick={handleDelete}
              type="button"
            >
              {busyAction === 'delete' ? 'Deleting…' : 'Delete'}
            </button>
            <button
              className={styles.updateBtn}
              disabled={isBusy}
              onClick={handleUpdate}
              type="button"
            >
              {busyAction === 'upload' ? 'Uploading…' : 'Update'}
            </button>
          </div>
        </div>
      </div>

      <div
        className={styles.uploadArea}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <div className={styles.uploadIcon}>
          <UploadIcon />
        </div>
        <p className={styles.uploadText}>
          <span className={styles.clickText} onClick={handleUpdate}>
            Click to upload
          </span>{' '}
          or drag and drop
        </p>
        <p className={styles.uploadHint}>
          SVG, PNG, JPG, Format (Recommended: 180x180px)
        </p>
      </div>

      <input
        accept="image/*"
        hidden
        onChange={handleFileSelect}
        ref={fileInputRef}
        type="file"
      />
    </div>
  )
}

export default ProfileImage
